import { useState, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { get, post, patch, del } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { EmptyState } from "@/components/common/EmptyState";
import { ConfirmDelete } from "@/components/common/ConfirmDelete";
import { money } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { ChevronRight, GripVertical, Plus, Pencil, Trash2, ListChecks, FileText } from "lucide-react";
import { toast } from "sonner";

export function RequirementsTab({ lead }) {
  const { can } = useApp();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [expanded, setExpanded] = useState({});
  const [reqModal, setReqModal] = useState(null); // { mode, data }
  const [featModal, setFeatModal] = useState(null); // { mode, requirementId, data }
  const [drag, setDrag] = useState(null);

  const { data: reqs = [], isLoading } = useQuery({
    queryKey: ["requirements", lead.id], queryFn: () => get(`/crm/leads/${lead.id}/requirements`),
  });
  const invalidate = () => qc.invalidateQueries({ queryKey: ["requirements", lead.id] });

  const createReq = useMutation({ mutationFn: (body) => post(`/crm/leads/${lead.id}/requirements`, body), onSuccess: () => { invalidate(); setReqModal(null); } });
  const updateReq = useMutation({ mutationFn: ({ id, body }) => patch(`/crm/requirements/${id}`, body), onSuccess: () => { invalidate(); setReqModal(null); } });
  const deleteReq = useMutation({ mutationFn: (id) => del(`/crm/requirements/${id}`), onSuccess: () => { invalidate(); toast.success("Requirement deleted"); } });
  const reorderReq = useMutation({ mutationFn: (order) => post(`/crm/leads/${lead.id}/requirements/reorder`, { order }), onSuccess: invalidate });

  const createFeat = useMutation({ mutationFn: ({ rid, body }) => post(`/crm/requirements/${rid}/features`, body), onSuccess: () => { invalidate(); setFeatModal(null); } });
  const updateFeat = useMutation({ mutationFn: ({ id, body }) => patch(`/crm/requirement-features/${id}`, body), onSuccess: () => { invalidate(); setFeatModal(null); } });
  const deleteFeat = useMutation({ mutationFn: (id) => del(`/crm/requirement-features/${id}`), onSuccess: () => { invalidate(); toast.success("Sub-feature deleted"); } });
  const reorderFeat = useMutation({ mutationFn: ({ rid, order }) => post(`/crm/requirements/${rid}/features/reorder`, { order }), onSuccess: invalidate });

  const sorted = [...reqs].sort((a, b) => a.order_index - b.order_index);
  const totalHours = sorted.reduce((s, r) => s + (Number(r.estimated_man_hours) || 0), 0);
  const totalCost = sorted.reduce((s, r) => s + (Number(r.cost_proposed) || 0), 0);

  const importIntoQuotation = async () => {
    let quo;
    const existing = await get(`/crm/quotations?lead_id=${lead.id}`);
    const draft = existing.find((q) => q.status === "draft");
    const lineItems = sorted.map((r) => ({
      id: crypto.randomUUID(), description: r.title, qty: 1, rate: Number(r.cost_proposed) || 0, tax: 0, discount: 0,
    }));
    if (draft) {
      quo = await patch(`/crm/quotations/${draft.id}`, { line_items: [...(draft.line_items || []), ...lineItems] });
    } else {
      quo = await post("/crm/quotations", { lead_id: lead.id, company_name: lead.company_name, currency: lead.currency || "USD", line_items: lineItems });
    }
    qc.invalidateQueries({ queryKey: ["quotations", lead.id] });
    navigate(`/crm/leads/${lead.id}/quotation/${quo.id}`);
  };

  if (isLoading) return null;

  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        {can("crm", "create") && (
          <Button size="sm" variant="outline" data-testid="add-requirement-btn" onClick={() => setReqModal({ mode: "new" })}>
            <Plus className="mr-1 h-3.5 w-3.5" />Add Requirement
          </Button>
        )}
        {sorted.length > 0 && can("crm", "create") && (
          <Button size="sm" variant="outline" data-testid="import-to-quotation-btn" onClick={importIntoQuotation}>
            <FileText className="mr-1 h-3.5 w-3.5" />Import into Quotation
          </Button>
        )}
      </div>

      {sorted.length === 0 ? <EmptyState icon={ListChecks} title="No requirements yet" /> : (
        <div className="overflow-hidden rounded-xl border border-border bg-card">
          <table className="w-full text-sm" data-testid="requirements-table">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="w-8"></th><th className="w-6"></th>
                <th className="px-3 py-2.5 font-semibold">Title</th>
                <th className="px-3 py-2.5 text-right font-semibold">Est. Hours</th>
                <th className="px-3 py-2.5 text-right font-semibold">Cost Proposed</th>
                <th className="px-3 py-2.5 text-right font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((r) => (
                <Fragment key={r.id}>
                  <tr key={r.id} data-testid={`requirement-row-${r.id}`}
                    draggable={can("crm", "edit")} onDragStart={() => setDrag(r.id)} onDragOver={(e) => e.preventDefault()}
                    onDrop={() => { if (drag && drag !== r.id) { const ids = sorted.map((x) => x.id); const from = ids.indexOf(drag), to = ids.indexOf(r.id); ids.splice(to, 0, ids.splice(from, 1)[0]); reorderReq.mutate(ids); } setDrag(null); }}
                    className={cn("border-b border-border hover:bg-muted/50", drag === r.id && "opacity-50")}>
                    <td className="pl-2"><GripVertical className="h-4 w-4 text-muted-foreground" /></td>
                    <td>
                      <button data-testid={`expand-req-${r.id}`} onClick={() => setExpanded((e) => ({ ...e, [r.id]: !e[r.id] }))}>
                        <ChevronRight className={cn("h-4 w-4 text-muted-foreground transition-transform", expanded[r.id] && "rotate-90")} />
                      </button>
                    </td>
                    <td className="px-3 py-2.5 font-medium">{r.title}</td>
                    <td className="px-3 py-2.5 text-right">{r.estimated_man_hours || 0}</td>
                    <td className="px-3 py-2.5 text-right">{money(r.cost_proposed, lead.currency)}</td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center justify-end gap-1">
                        {can("crm", "edit") && (
                          <Button variant="ghost" size="icon" data-testid={`edit-req-${r.id}`} onClick={() => setReqModal({ mode: "edit", data: r })}>
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        {can("crm", "delete") && (
                          <ConfirmDelete testid={`del-req-${r.id}`} title={`Delete "${r.title}"?`}
                            trigger={<Button variant="ghost" size="icon"><Trash2 className="h-3.5 w-3.5" /></Button>}
                            onConfirm={() => deleteReq.mutate(r.id)} />
                        )}
                      </div>
                    </td>
                  </tr>
                  {expanded[r.id] && (
                    <tr key={`${r.id}-features`}>
                      <td colSpan={6} className="bg-muted/30 px-3 py-2">
                        <div className="ml-10 space-y-1">
                          {[...(r.features || [])].sort((a, b) => a.order_index - b.order_index).map((f) => (
                            <div key={f.id} data-testid={`feature-row-${f.id}`}
                              draggable={can("crm", "edit")} onDragStart={() => setDrag(f.id)} onDragOver={(e) => e.preventDefault()}
                              onDrop={() => { if (drag && drag !== f.id) { const ids = r.features.map((x) => x.id); const from = ids.indexOf(drag), to = ids.indexOf(f.id); ids.splice(to, 0, ids.splice(from, 1)[0]); reorderFeat.mutate({ rid: r.id, order: ids }); } setDrag(null); }}
                              className="group flex items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-card">
                              <GripVertical className="h-3.5 w-3.5 text-muted-foreground" />
                              <span className="flex-1">{f.title}</span>
                              <span className="text-xs text-muted-foreground">{f.estimated_man_hours ? `${f.estimated_man_hours}h` : ""}</span>
                              {can("crm", "edit") && (
                                <div className="hidden items-center gap-0.5 group-hover:flex">
                                  <button onClick={() => setFeatModal({ mode: "edit", requirementId: r.id, data: f })} className="rounded p-1 text-muted-foreground hover:bg-muted"><Pencil className="h-3 w-3" /></button>
                                  <button onClick={() => deleteFeat.mutate(f.id)} className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-red-600"><Trash2 className="h-3 w-3" /></button>
                                </div>
                              )}
                            </div>
                          ))}
                          {can("crm", "create") && (
                            <button data-testid={`add-feature-${r.id}`} onClick={() => setFeatModal({ mode: "new", requirementId: r.id })}
                              className="ml-5 mt-1 flex items-center gap-1 text-xs text-primary hover:underline">
                              <Plus className="h-3 w-3" />Add Sub-Feature
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-border font-semibold">
                <td colSpan={3} className="px-3 py-2.5 text-right">Total</td>
                <td className="px-3 py-2.5 text-right">{totalHours}h</td>
                <td className="px-3 py-2.5 text-right">{money(totalCost, lead.currency)}</td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      <RequirementModal state={reqModal} setState={setReqModal}
        onSubmit={(body) => reqModal?.mode === "new" ? createReq.mutate(body) : updateReq.mutate({ id: reqModal.data.id, body })} />
      <FeatureModal state={featModal} setState={setFeatModal}
        onSubmit={(body) => featModal?.mode === "new"
          ? createFeat.mutate({ rid: featModal.requirementId, body })
          : updateFeat.mutate({ id: featModal.data.id, body })} />
    </div>
  );
}

function RequirementModal({ state, setState, onSubmit }) {
  const [form, setForm] = useState({});
  const d = state?.data || {};
  const val = (k) => (form[k] !== undefined ? form[k] : d[k]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  return (
    <Dialog open={!!state} onOpenChange={(v) => { if (!v) setState(null); else setForm({}); }} key={d.id || "new-req"}>
      <DialogContent className="max-w-md" data-testid="requirement-modal">
        <DialogHeader><DialogTitle>{state?.mode === "edit" ? "Edit Requirement" : "Add Requirement"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Title *</Label><Input data-testid="req-title" className="mt-1" value={val("title") || ""} onChange={(e) => set("title", e.target.value)} /></div>
          <div><Label>Description</Label><Textarea className="mt-1" value={val("description") || ""} onChange={(e) => set("description", e.target.value)} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Est. Man Hours</Label><Input type="number" data-testid="req-hours" className="mt-1" value={val("estimated_man_hours") ?? ""} onChange={(e) => set("estimated_man_hours", Number(e.target.value))} /></div>
            <div><Label>Cost Proposed</Label><Input type="number" data-testid="req-cost" className="mt-1" value={val("cost_proposed") ?? ""} onChange={(e) => set("cost_proposed", Number(e.target.value))} /></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setState(null)}>Cancel</Button>
          <Button data-testid="req-submit" onClick={() => { if (!val("title")) { toast.error("Title is required"); return; } onSubmit({ title: val("title"), description: val("description") || "", estimated_man_hours: val("estimated_man_hours") || 0, cost_proposed: val("cost_proposed") || 0 }); }}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FeatureModal({ state, setState, onSubmit }) {
  const [form, setForm] = useState({});
  const d = state?.data || {};
  const val = (k) => (form[k] !== undefined ? form[k] : d[k]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  return (
    <Dialog open={!!state} onOpenChange={(v) => { if (!v) setState(null); else setForm({}); }} key={d.id || "new-feat"}>
      <DialogContent className="max-w-sm" data-testid="feature-modal">
        <DialogHeader><DialogTitle>{state?.mode === "edit" ? "Edit Sub-Feature" : "Add Sub-Feature"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Title *</Label><Input data-testid="feat-title" className="mt-1" value={val("title") || ""} onChange={(e) => set("title", e.target.value)} /></div>
          <div><Label>Description</Label><Textarea className="mt-1" value={val("description") || ""} onChange={(e) => set("description", e.target.value)} /></div>
          <div><Label>Est. Man Hours (optional)</Label><Input type="number" data-testid="feat-hours" className="mt-1" value={val("estimated_man_hours") ?? ""} onChange={(e) => set("estimated_man_hours", e.target.value === "" ? null : Number(e.target.value))} /></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setState(null)}>Cancel</Button>
          <Button data-testid="feat-submit" onClick={() => { if (!val("title")) { toast.error("Title is required"); return; } onSubmit({ title: val("title"), description: val("description") || "", estimated_man_hours: val("estimated_man_hours") ?? null }); }}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
